/**
 * Copyright Highway9 Networks Inc.
 */
import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { userService } from "~/services";
import type { Role } from "~/types/Roles";
import type { RootState } from "..";

export type RoleErrorItem = { message: string };

type RolesRejection = { errors?: RoleErrorItem[]; warnings?: RoleErrorItem[] };

type initState = {
  data: Role[];
  loading: boolean;
  loaded: boolean;
  errors: RoleErrorItem[];
  warnings: RoleErrorItem[];
};

const initialState: initState = {
  data: [],
  loading: false,
  loaded: false,
  errors: [],
  warnings: [],
};

const toErrorItems = (items: unknown): RoleErrorItem[] => {
  if (!Array.isArray(items)) return [];
  return items
    .filter((item): item is { message: string } => typeof (item as { message?: unknown })?.message === "string")
    .map((item) => ({ message: item.message }));
};

const fallbackErrors = (error: unknown): RoleErrorItem[] => {
  if (error && typeof error === "object" && "message" in error && typeof (error as { message: unknown }).message === "string") {
    return [{ message: (error as { message: string }).message }];
  }
  return [{ message: "Failed to load roles." }];
};

export const fetchRoles = createAsyncThunk<
  Role[],
  void,
  { state: RootState; rejectValue: RolesRejection }
>("roles/fetchRoles", async (_, { rejectWithValue }) => {
  try {
    const data = await userService.getUserRoles();
    const errSrc = (data ?? {}) as RolesRejection;
    if (errSrc.errors?.length || errSrc.warnings?.length) {
      return rejectWithValue({
        errors: toErrorItems(errSrc.errors),
        warnings: toErrorItems(errSrc.warnings),
      });
    }
    return (Array.isArray(data) ? data : []) as Role[];
  } catch (error) {
    const errSrc = (error ?? {}) as RolesRejection;
    return rejectWithValue({
      errors: toErrorItems(errSrc.errors).length ? toErrorItems(errSrc.errors) : fallbackErrors(error),
      warnings: toErrorItems(errSrc.warnings),
    });
  }
}, {
  condition: (_, { getState }) => {
    const { loading, loaded } = getState().roles;
    if (loading || loaded) return false;
  },
});

const rolesSlice = createSlice({
  name: "roles",
  initialState,
  reducers: {
    dismissError: (state, action: PayloadAction<number>) => {
      state.errors = state.errors.filter((_, i) => i !== action.payload);
    },
    dismissWarning: (state, action: PayloadAction<number>) => {
      state.warnings = state.warnings.filter((_, i) => i !== action.payload);
    },
    dismissAll: (state) => {
      state.errors = [];
      state.warnings = [];
    },
    reset: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchRoles.pending, (state) => {
        state.loading = true;
        state.errors = [];
        state.warnings = [];
      })
      .addCase(fetchRoles.fulfilled, (state, action) => {
        state.loading = false;
        state.loaded = true;
        state.data = action.payload;
      })
      .addCase(fetchRoles.rejected, (state, action) => {
        state.loading = false;
        state.errors = action.payload?.errors ?? [{ message: action.error.message ?? "Failed to load roles." }];
        state.warnings = action.payload?.warnings ?? [];
      });
  },
});

export const rolesActions = rolesSlice.actions;
export default rolesSlice.reducer;

export const selectRoles = (state: RootState) => state.roles.data;
export const selectRolesLoading = (state: RootState) => state.roles.loading;
export const selectRolesLoaded = (state: RootState) => state.roles.loaded;
export const selectRolesErrors = (state: RootState) => state.roles.errors;
export const selectRolesWarnings = (state: RootState) => state.roles.warnings;
